import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Loader2, MessageSquareText, Plus, Pencil, Trash2 } from "lucide-react";
import {
  messageTemplateService,
  MESSAGE_TEMPLATE_TYPE_LABEL,
  MessageTemplate,
} from "@/services/messageTemplate";
import { CreateMessageTemplateModal } from "@/components/templates/CreateMessageTemplateModal";
import { UpdateMessageTemplateModal } from "@/components/templates/UpdateMessageTemplateModal";

interface MessageTemplatesTabProps {
  templates: MessageTemplate[];
  isLoading: boolean;
}

export function MessageTemplatesTab({ templates, isLoading }: MessageTemplatesTabProps) {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [editTemplate, setEditTemplate] = useState<MessageTemplate | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (id: number) => messageTemplateService.remove(id),
    onSuccess: () => {
      toast.success("Plantilla eliminada exitosamente");
      queryClient.invalidateQueries({ queryKey: ["message-templates"] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <div>
      <div className="flex items-center justify-end mb-4">
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="w-3.5 h-3.5 mr-1.5" />
          Nueva plantilla
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : templates.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 gap-2">
          <MessageSquareText className="w-8 h-8 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">Sin plantillas de mensaje</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Variables</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {templates.map((tpl) => (
                <TableRow key={tpl.id} className="hover:bg-muted/30">
                  <TableCell className="font-medium text-foreground">{tpl.name}</TableCell>
                  <TableCell className="text-muted-foreground text-xs">
                    {MESSAGE_TEMPLATE_TYPE_LABEL[tpl.type] ?? tpl.type}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1 max-w-[220px]">
                      {tpl.variables.slice(0, 3).map((v) => (
                        <Badge key={v} variant="secondary" className="font-mono text-[10px]">
                          {v}
                        </Badge>
                      ))}
                      {tpl.variables.length > 3 && (
                        <Badge variant="outline" className="text-[10px]">
                          +{tpl.variables.length - 3}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={
                        tpl.isActive
                          ? "bg-emerald-500/15 text-emerald-500 border-emerald-500/30"
                          : "bg-muted text-muted-foreground border-border"
                      }
                    >
                      {tpl.isActive ? "Activa" : "Inactiva"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setEditTemplate(tpl)}
                    >
                      <Pencil className="h-4 w-4 text-muted-foreground" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>¿Eliminar plantilla?</AlertDialogTitle>
                          <AlertDialogDescription>
                            La plantilla <strong>{tpl.name}</strong> será eliminada permanentemente. Esta acción no se puede deshacer.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => deleteMutation.mutate(tpl.id)}
                            disabled={deleteMutation.isPending}
                            className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
                          >
                            Eliminar
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <CreateMessageTemplateModal open={createOpen} onOpenChange={setCreateOpen} />
      {editTemplate && (
        <UpdateMessageTemplateModal
          open={!!editTemplate}
          onOpenChange={(open) => !open && setEditTemplate(null)}
          template={editTemplate}
        />
      )}
    </div>
  );
}
